import { z } from "zod";
import {
  ClassificationFieldsSchema,
  ClassificationTypeSchema,
} from "./Classification.js";
import { FactRationaleSchema } from "./Fact.js";
import {
  factInsightSeverities,
  FactInsightStatusSchema,
} from "./FactInsight.js";

/**
 * ========================================
 * NOAN INTELLIGENCE SCHEMAS
 * ========================================
 */

export const intelligenceScoreLevels = [
  "unrated",
  "bronze",
  "silver",
  "gold",
  "platinum",
] as const;

export const NoanIntelligenceScoreResponseSchema = z.object({
  intelligenceScore: z.number().min(0).max(100),
  level: z.enum(intelligenceScoreLevels),
  organizationSummary: z.string(),
});

export type NoanIntelligenceScoreResponse = z.infer<
  typeof NoanIntelligenceScoreResponseSchema
>;
export type IntelligenceScoreLevel = (typeof intelligenceScoreLevels)[number];

export function getIntelligenceScoreLevel(
  score: number,
): IntelligenceScoreLevel {
  if (score >= 80) return "platinum";
  if (score >= 60) return "gold";
  if (score >= 40) return "silver";
  if (score >= 20) return "bronze";
  return "unrated";
}

/**
 * ========================================
 * NOAN CONTEXT GRAPH SCHEMAS
 * ========================================
 */

export const GraphNodeSchema = z.object({
  id: z.string(),
  organizationId: z.string().optional(),
  sourceId: z.string().optional(),
  label: z.string().optional(),
  name: z.string().optional(),
  title: z.string().optional(),
  content: z.string().optional(),
  description: z.string().optional(),
  tooltip: z
    .union([
      z.string(),
      z.object({ title: z.string().optional(), label: z.string().optional() }),
    ])
    .optional(),
  nodeGroup: z.string().optional(),
  nodeType: z.string().optional(),
  createdByDisplay: z.string().optional(),
  blockPath: z.string().optional(),
  tags: z.array(z.string()).optional(),
  tagIds: z.array(z.string()).optional(),
  degree: z.number().optional(),
  metadata: z
    .object({
      creatorId: z.string().optional(),
      isPrivate: z.boolean().optional(),
      createdByAssistant: z.boolean().optional(),
      decisionAt: z.string().optional(),
      rationale: FactRationaleSchema.nullish(),
    })
    .optional(),
  classification: ClassificationFieldsSchema.nullable().optional(),
});

export const GraphLinkSchema = z.object({
  id: z.string().optional(),
  source: z.string(),
  target: z.string(),
  kind: z.string().optional(),
  edgeType: z.string().optional(),
  score: z.number().optional(),
  weight: z.number().optional(),
  reasons: z.array(z.string()).optional(),
});

export const TimeSliceSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  startAt: z.string().optional(),
  endAt: z.string().optional(),
  nodeCount: z.number().optional(),
  linkCount: z.number().optional(),
});

/**
 * Slice nodes only carry the id and the slice degree, the rest is in the registry
 */
export const GraphNodeRefSchema = z.object({
  id: z.string(),
  degree: z.number().optional(),
});

export const GraphDataSchema = z.object({
  nodes: z.array(GraphNodeRefSchema),
  links: z.array(GraphLinkSchema),
});

export const NodeStyleSchema = z.object({
  color: z.string().optional(),
  size: z.number().optional(),
  opacity: z.number().optional(),
  glow: z.number().optional(),
  shape: z.string().optional(),
});

export const LinkStyleSchema = z.object({
  color: z.string(),
  width: z.number(),
  opacity: z.number(),
  curvature: z.number().optional(),
});

export const ContextGraphSchema = z.object({
  timeSlices: z.array(TimeSliceSchema).optional(),
  legend: z
    .object({
      nodeStyle: z.record(z.string(), NodeStyleSchema).optional(),
      edgeStyle: z.record(z.string(), LinkStyleSchema).optional(),
    })
    .optional(),
  nodeRegistry: z.record(z.string(), GraphNodeSchema),
  graphsBySlice: z.record(z.string(), GraphDataSchema).optional(),
});

export type GraphNode = z.infer<typeof GraphNodeSchema>;
export type GraphNodeRef = z.infer<typeof GraphNodeRefSchema>;
export type GraphLink = z.infer<typeof GraphLinkSchema>;
export type TimeSlice = z.infer<typeof TimeSliceSchema>;
export type NodeStyle = z.infer<typeof NodeStyleSchema>;
export type LinkStyle = z.infer<typeof LinkStyleSchema>;
export type ContextGraph = z.infer<typeof ContextGraphSchema>;

const MLGraphNodeSchema = z.object({
  id: z.string(),
  organization_id: z.string().optional(),
  source_id: z.string().optional(),
  label: z.string().optional(),
  name: z.string().optional(),
  title: z.string().optional(),
  content: z.string().optional(),
  description: z.string().optional(),
  tooltip: z
    .union([
      z.string(),
      z.object({ title: z.string().optional(), label: z.string().optional() }),
    ])
    .optional(),
  node_group: z.string().optional(),
  node_type: z.string().optional(),
  created_by_display: z.string().optional(),
  block_path: z.string().optional(),
  tags: z.array(z.string()).optional(),
  tag_ids: z.array(z.string()).optional(),
  degree: z.number().optional(),
  metadata: z
    .object({
      creator_id: z.string().optional(),
      is_private: z.boolean().optional(),
      created_by_assistant: z.boolean().optional(),
      decision_at: z.string().optional(),
      rationale: FactRationaleSchema.nullish(),
    })
    .optional(),
  classification: z
    .object({
      type: ClassificationTypeSchema,
      secondary_types: z.array(ClassificationTypeSchema),
      subject: z.string(),
    })
    .nullable()
    .optional(),
});

const MLGraphLinkSchema = z.object({
  id: z.string().optional(),
  source: z.string(),
  target: z.string(),
  kind: z.string().optional(),
  edge_type: z.string().optional(),
  score: z.number().optional(),
  weight: z.number().optional(),
  reasons: z.array(z.string()).optional(),
});

const MLTimeSliceSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  start_at: z.string().optional(),
  end_at: z.string().optional(),
  node_count: z.number().optional(),
  link_count: z.number().optional(),
  fact_metrics: z.record(z.string(), z.unknown()).optional(),
});

const MLGraphDataSchema = z.object({
  nodes: z.array(GraphNodeRefSchema),
  links: z.array(MLGraphLinkSchema),
});

export const MLContextGraphResponseSchema = z.object({
  meta: z.record(z.string(), z.unknown()).optional(),
  time_slices: z.array(MLTimeSliceSchema).optional(),
  legend: z
    .object({
      node_style: z.record(z.string(), NodeStyleSchema).optional(),
      edge_style: z.record(z.string(), LinkStyleSchema).optional(),
    })
    .optional(),
  interaction_hints: z.record(z.string(), z.unknown()).optional(),
  node_registry: z.record(z.string(), MLGraphNodeSchema),
  graphs_by_slice: z.record(z.string(), MLGraphDataSchema).optional(),
});

export type MLContextGraphResponse = z.infer<
  typeof MLContextGraphResponseSchema
>;

/**
 * ========================================
 * NOAN CONTEXT GRAPH INSIGHTS SCHEMAS
 * ========================================
 */

export const MLFactInsightMetadataSchema = z
  .object({
    left_fact_id: z.string(),
    right_fact_id: z.string(),
    left_block_id: z.string(),
    right_block_id: z.string(),
    left_block_path: z.string(),
    right_block_path: z.string(),
    left_block_title: z.string(),
    right_block_title: z.string(),
    left_stack_slug: z.string(),
    left_stack_title: z.string(),
    right_stack_slug: z.string(),
    right_stack_title: z.string(),
    similarity: z.number(),
    min_similarity: z.number(),
    max_similarity: z.number(),
    distance_to_threshold: z.number(),
    cross_root: z.boolean(),
    both_block_descriptions: z.boolean(),
    root_pair: z.tuple([z.string(), z.string()]),
    llm_status: z.string(),
    llm_action: z.string(),
    llm_confidence: z.string(),
    llm_reason: z.string(),
    llm_user_suggestion: z.string(),
  })
  .partial()
  .passthrough();

export const MLFactInsightSchema = z.object({
  type: z.string(),
  severity: z.enum(factInsightSeverities),
  title: z.string(),
  description: z.string(),
  node_ids: z.array(z.string()),
  suggestion: z.string(),
  metadata: MLFactInsightMetadataSchema.optional(),
});

export const MLBusinessAreaGapMetadataSchema = z.looseObject({
  business_area: z.string(),
  coverage: z.looseObject({
    covered_subtopics: z.number(),
    total_subtopics: z.number(),
  }),
  subtopics: z.array(
    z.looseObject({
      key: z.string(),
      title: z.string(),
      status: z.string(),
      match_method: z.string(),
      semantic_similarity: z.number().nullable(),
      semantic_score_margin: z.number().nullable(),
      evidence_block_ids: z.array(z.string()),
    }),
  ),
  suggested_blocks: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      definition: z.string(),
      destination_stack: z.object({
        title: z.string(),
        existing_stack_slug: z.string().nullable(),
        existing_stack_id: z.string().nullable().optional(),
      }),
    }),
  ),
  existing_blocks_to_complete: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      block_ids: z.array(z.string()).optional(),
      block_paths: z.array(z.string()),
    }),
  ),
});

export const mlInsightsRunStatuses = ["complete", "capped", "failed"] as const;

export const MLFactInsightsResponseSchema = z.object({
  health: z.record(z.string(), z.unknown()).optional(),
  /** ML does not send it yet, missing means a partial run */
  status: z.enum(mlInsightsRunStatuses).optional(),
  insights: z.array(MLFactInsightSchema),
});

export const MLInsightWordingSchema = z.union([
  z.object({
    action: z.string(),
    confidence: z.string(),
    reason: z.string(),
    user_suggestion: z.string(),
  }),
  z.object({
    suggested_blocks: z.array(
      z.object({ key: z.string(), title: z.string(), definition: z.string() }),
    ),
  }),
]);

export const MLSavedInsightWordingSchema = z.object({
  fingerprint: z.string(),
  organization_id: z.uuid(),
  node_ids: z.array(z.string()),
  status: FactInsightStatusSchema,
  input_hash: z.string().nullable(),
  wording: MLInsightWordingSchema.nullable(),
});

export type MLFactInsightMetadata = z.infer<typeof MLFactInsightMetadataSchema>;
export type MLInsightWording = z.infer<typeof MLInsightWordingSchema>;
export type MLSavedInsightWording = z.infer<typeof MLSavedInsightWordingSchema>;
export type MLFactInsight = z.infer<typeof MLFactInsightSchema>;
export type MLBusinessAreaGapMetadata = z.infer<
  typeof MLBusinessAreaGapMetadataSchema
>;
export type MLInsightsRunStatus = (typeof mlInsightsRunStatuses)[number];
export type MLFactInsightsResponse = z.infer<
  typeof MLFactInsightsResponseSchema
>;

/**
 * ========================================
 * FACT EXTRACTION SCHEMAS
 * ========================================
 */

export const maxDocumentUploadBytes = 50 * 1024 * 1024; // 50MB

export const FactExtractionStatusSchema = z.enum([
  "pending",
  "accepted",
  "rejected",
  "skipped",
]);
export type FactExtractionStatus = z.infer<typeof FactExtractionStatusSchema>;

export const FactCandidateSchema = z.object({
  id: z.string().uuid(),
  blockId: z.uuid().optional(),
  blockPath: z.string(),
  plainText: z.string(),
});

export const FactExtractionSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  createdAt: z.coerce.date(),

  factCandidate: FactCandidateSchema,

  extractionType: z.string(),
  confidence: z.number(),
  meanChunkSimilarity: z.number(),
  numChunksSelected: z.number(),
  sourceDocument: z.string(),
  promptTemplateUsed: z.string(),
  chunkMetadata: z.record(z.string(), z.unknown()),
  pipelineSettings: z.record(z.string(), z.unknown()),

  status: FactExtractionStatusSchema,

  reviewerId: z.string().nullable(),
  reviewerType: z.string(),
  reviewedAt: z.coerce.date().nullable(),
  reviewDurationSeconds: z.number().nullable(),
});

export type FactCandidate = z.infer<typeof FactCandidateSchema>;
export type FactExtraction = z.infer<typeof FactExtractionSchema>;

export const FactExtractionReviewDecisionSchema = z.object({
  extractionId: z.string().uuid(),
  factId: z.string().uuid(),
  blockId: z.uuid().optional(),
  blockPath: z.string(),
  status: FactExtractionStatusSchema.exclude(["pending"]),
  plainText: z.string(),
  reviewDurationSeconds: z.number().optional(),
});

export type FactExtractionReviewDecision = z.infer<
  typeof FactExtractionReviewDecisionSchema
>;

export const MLFactExtractionSchema = z.object({
  id: z.uuid(),
  organization_id: z.string(),
  extraction_type: z.string(),
  confidence: z.number(),
  mean_chunk_similarity: z.number(),
  num_chunks_selected: z.number(),
  source_document: z.string(),
  prompt_template_used: z.string(),
  chunk_metadata: z.record(z.string(), z.unknown()),
  pipeline_settings: z.record(z.string(), z.unknown()),
  status: z.string(),
  fact_candidate: z.record(z.string(), z.unknown()),
});

export const MLExtractFactsResponseSchema = z.object({
  organization_id: z.string(),
  documents_processed: z.number(),
  facts_extracted: z.number(),
  extractions: z.array(MLFactExtractionSchema),
});

export type MLFactExtraction = z.infer<typeof MLFactExtractionSchema>;
export type MLExtractFactsResponse = z.infer<
  typeof MLExtractFactsResponseSchema
>;

export const ExtractFactsResponseSchema = z.object({
  documentsProcessed: z.number(),
  factsExtracted: z.number(),
});

export type ExtractFactsResponse = z.infer<typeof ExtractFactsResponseSchema>;

/**
 * ========================================
 * CLASSIFICATION SCHEMAS
 * ========================================
 */

export const MLClassifyRequestSchema = z.object({
  source_type: z.string(),
  source_id: z.string(),
  body: z.string(),
  title: z.string().optional(),
  stack: z.string().optional(),
  fixed_type: z.string().optional(),
  known_subjects: z.array(z.string()),
});

export const MLClassifyResultSchema = z.object({
  source_type: z.string(),
  source_id: z.string(),
  primary_type: z.string(),
  secondary_types: z.array(z.string()),
  subject: z.string(),
  classification_version: z.string(),
});

export const MLClassifyBatchResponseSchema = z.object({
  items: z.array(
    z.object({
      index: z.number(),
      source_id: z.string(),
      result: MLClassifyResultSchema.nullable().optional(),
      error: z.string().nullable().optional(),
    }),
  ),
  succeeded: z.number(),
  failed: z.number(),
});

export type MLClassifyRequest = z.infer<typeof MLClassifyRequestSchema>;
export type MLClassifyResult = z.infer<typeof MLClassifyResultSchema>;
export type MLClassifyBatchResponse = z.infer<
  typeof MLClassifyBatchResponseSchema
>;
